import type { ReactNode } from "react";
import { Link, Navigate } from "react-router";
import {
  ArrowRight, Brain, Check, Clock, Compass, Database, EyeOff, FileCheck2, Globe2, KeyRound, Lock, Network, Plug, Receipt, Route, ScanSearch,
  ShieldCheck, Sparkles, Store, Zap,
} from "lucide-react";
import { ButtonLink, CodeBlock, Badge } from "../../components/ui";
import { DataNetwork } from "../../components/marketing/DataNetwork";
import { RevenueCalculator } from "../../components/marketing/RevenueCalculator";
import { PublicSearch } from "../../components/marketing/PublicSearch";
import { Flywheel } from "../../components/marketing/Flywheel";
import { usePageView } from "../../lib/gtm";
import { useTitle } from "../gtm/shared";
import { useSession } from "../../lib/session";

function Section({ id, eyebrow, title, lede, children, className }: { id?: string; eyebrow: string; title: ReactNode; lede?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section id={id} className={`border-t border-border py-20 ${className ?? ""}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">{eyebrow}</p>
        <h2 className="mt-3 max-w-3xl text-3xl font-semibold tracking-tight text-fg sm:text-4xl">{title}</h2>
        {lede && <p className="mt-4 max-w-2xl text-base leading-relaxed text-fg-2">{lede}</p>}
        <div className="mt-12">{children}</div>
      </div>
    </section>
  );
}

function Feature({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-panel p-5">
      <div className="mb-3 flex size-9 items-center justify-center rounded-md bg-accent-soft text-accent" aria-hidden>
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-fg">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-fg-2">{children}</p>
    </div>
  );
}

const MAPPING: [string, string, "shared" | "transformed" | "blocked" | "prohibited"][] = [
  ["product.id", "product.id", "shared"],
  ["product.name", "product.name", "shared"],
  ["product.price", "product.price", "shared"],
  ["inventory.quantity", "availability.quantity", "transformed"],
  ["inventory.warehouse_id", "availability.region", "transformed"],
  ["customer.email", "BLOCKED", "blocked"],
  ["customer.phone", "BLOCKED", "blocked"],
  ["payment.card_number", "PROHIBITED", "prohibited"],
  ["internal.margin", "BLOCKED", "blocked"],
];

const CURL = `curl https://api.dbindex.ai/v1/query \\
  -H "Authorization: Bearer dbi_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "question": "What products under $500 are currently available in Sydney?",
    "constraints": { "freshness_seconds": 60, "max_query_price": "0.002000" }
  }'`;

const RESPONSE = `{
  "data": { "columns": ["product_id","name","price","region"], "rows": [...], "row_count": 25 },
  "provenance": {
    "dataset_id": "dst_01J…", "provider": "Example Retail",
    "source_timestamp": "2026-10-16T05:12:04Z", "freshness_seconds": 3,
    "quality_score": 0.96, "license": "dbindex-standard-query-1.0"
  },
  "billing": { "query_price": "0.001200", "currency": "USD" },
  "request_id": "req_01J…"
}`;

const MCP = `{
  "mcpServers": {
    "dbindex": {
      "url": "https://api.dbindex.ai/mcp",
      "headers": { "Authorization": "Bearer dbi_live_..." }
    }
  }
}`;

export function Home() {
  usePageView("home");
  useTitle("The data network for AI agents");
  // Signed in: the app's front door is searching your own connected databases, not the marketing page.
  const { me, membership } = useSession();
  if (me && membership) return <Navigate to={membership.kinds.includes("provider") ? "/ask" : "/consumer"} replace />;
  return (
    <>
      {/* Hero — the network first (§44, §78) */}
      <section className="relative overflow-hidden">
        <div className="grid-bg pointer-events-none absolute inset-0 opacity-[0.35] [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pb-16 pt-20 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:pt-24">
          <div>
            <Badge tone="accent" className="mb-6">Live proprietary data · One API · MCP-native</Badge>
            <h1 className="max-w-4xl text-4xl font-semibold leading-[1.06] tracking-tight text-fg sm:text-6xl">
              The data network <span className="text-accent">for AI agents.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-fg-2">
              Discover and query live proprietary data through one API. Data providers control what they share and earn whenever AI uses it.
            </p>
            <PublicSearch />
            <div className="mt-8 flex flex-wrap gap-3">
              <ButtonLink to="/discover" variant="primary" size="lg">
                Explore Live Data <ArrowRight className="size-4" aria-hidden />
              </ButtonLink>
              <ButtonLink to="/partners/founding-data" variant="outline" size="lg">
                Publish Your Data
              </ButtonLink>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-panel p-3 sm:p-5">
            <Flywheel className="h-auto w-full" />
            <p className="mt-2 text-center text-xs text-fg-3">More data → better coverage → more developers → more queries → more provider earnings → more demand. Unanswered demand tells us which data to add next.</p>
          </div>
        </div>
        <div className="border-y border-border bg-bg-elev">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-8 gap-y-3 px-4 py-4 text-xs text-fg-2 sm:px-6">
            {[
              [Compass, "Automatic discovery"],
              [Route, "Explainable routing"],
              [Receipt, "Pay per query"],
              [ShieldCheck, "Providers approve every field"],
              [Lock, "Production databases never exposed"],
            ].map(([Icon, t]) => {
              const I = Icon as typeof Lock;
              return (
                <span key={t as string} className="flex items-center gap-2">
                  <I className="size-4 text-fg-3" aria-hidden />
                  {t as string}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* Two sides of one network */}
      <section className="py-16">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-2">
          <SideCard
            eyebrow="For AI builders"
            lines={["One API.", "Live data.", "Automatic discovery and routing."]}
            body="Stop integrating one data API at a time. Ask a question; DBIndex finds the source, routes the query within your price and freshness limits, and handles payment."
            cta={["Start querying", "/developers"]}
            icon={<Sparkles className="size-5" />}
          />
          <SideCard
            eyebrow="For data owners"
            lines={["Publish safely.", "Set your price.", "Earn from every query."]}
            body="Turn your existing data product into an AI-native one. You approve exactly which fields AI can query; DBIndex meters every query and pays you."
            cta={["Publish data", "/partners/founding-data"]}
            icon={<Store className="size-5" />}
          />
        </div>
      </section>

      {/* Commerce wedge (§3) — the first template, not a special case */}
      <Section eyebrow="Starting with commerce" title="Product, price and availability — live." lede="The first network template is commerce and product intelligence. The platform is horizontal: travel, property, logistics, energy and more use the same discovery, routing and billing.">
        <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
          <div className="rounded-lg border border-border bg-panel p-5">
            <p className="font-mono text-xs uppercase tracking-wider text-fg-3">Agent request</p>
            <p className="mt-2 text-lg font-medium text-fg">“Find a Sony 85-inch television available for delivery tomorrow in Sydney for under A$3,000.”</p>
            <ul className="mt-4 space-y-2 text-sm text-fg-2">
              {["Discover datasets covering product, price, availability and delivery in Sydney", "Rank by freshness, coverage, quality, latency and price", "Query the winner; return rows with provenance and price"].map((t) => (
                <li key={t} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />{t}</li>
              ))}
            </ul>
            <ButtonLink to={`/discover?q=${encodeURIComponent("Find a Sony 85-inch television available for delivery tomorrow in Sydney for under A$3,000")}`} variant="outline" size="sm" className="mt-5">Try it on the live network <ArrowRight className="size-3.5" /></ButtonLink>
          </div>
          <div className="rounded-lg border border-border bg-panel p-5">
            <p className="font-mono text-xs uppercase tracking-wider text-fg-3">AI is looking for this data</p>
            <p className="mt-2 text-sm text-fg-2">Every question the network can't answer becomes an anonymised demand signal. Data owners see exactly what agents are asking for — and where nobody supplies it yet.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <ButtonLink to="/data-wanted" variant="primary" size="sm">See data wanted</ButtonLink>
              <ButtonLink to="/network" variant="ghost" size="sm">Network stats</ButtonLink>
            </div>
          </div>
        </div>
      </Section>

      {/* Live architecture */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="overflow-x-auto rounded-xl border border-border bg-panel p-4 sm:p-6">
            <DataNetwork className="h-auto w-full min-w-[760px]" />
          </div>
          <p className="mt-3 text-center text-xs text-fg-3">Under the hood: source databases stay in the provider's network. Only approved, sanitised fields are synchronised into DBIndex serving projections.</p>
        </div>
      </section>

      {/* How it works */}
      <Section id="how" eyebrow="How it works" title="Two sides of one network." lede="Data owners publish sanitised serving projections. AI systems discover, route to and query them through one vendor-neutral API.">
        <div className="grid gap-6 lg:grid-cols-2">
          <Flow
            id="owners"
            title="For data owners"
            steps={[
              ["Connect", "Install the outbound-only connector with a read-only account. No inbound ports."],
              ["Sanitise", "The AI Data Architect classifies every column. PII and secrets are blocked by default."],
              ["Publish", "Approve an exact Data Exposure Contract. DBIndex builds the serving projection and manifest."],
              ["Earn", "Set a price per query. Every query is metered on an immutable ledger."],
            ]}
          />
          <Flow
            id="ai"
            title="For AI"
            steps={[
              ["Discover", "Ask in natural language. The global catalogue finds datasets able to answer — no provider scanning."],
              ["Route", "Candidates are ranked by freshness, quality, latency, price and your constraints."],
              ["Query", "A typed, validated query plan runs against the serving projection — never raw SQL."],
              ["Pay", "Per-query pricing within your budget, with full provenance on every response."],
            ]}
          />
        </div>
      </Section>

      {/* Security */}
      <Section
        eyebrow="Your database stays private"
        title="Source database ≠ serving database."
        lede="DBIndex never queries your production database on behalf of an AI model. It serves an independent, purpose-built projection containing only the fields you approved."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <Feature icon={<Plug className="size-4" />} title="Outbound-only connector">
            Runs inside your network with a dedicated read-only account. Database credentials never leave your environment; DBIndex stores only the connector's public key.
          </Feature>
          <Feature icon={<EyeOff className="size-4" />} title="Default-deny sensitive data">
            PII, financial, health and secret fields are blocked until a security reviewer explicitly decides otherwise. New columns arrive blocked.
          </Feature>
          <Feature icon={<FileCheck2 className="size-4" />} title="Signed exposure contracts">
            Every exposure is a versioned, signed contract the connector enforces locally before data leaves — and you can run "What leaves my network?" at any time.
          </Feature>
          <Feature icon={<ScanSearch className="size-4" />} title="No arbitrary SQL">
            Natural language resolves to a typed plan validated against approved capabilities. LLM output never becomes SQL.
          </Feature>
          <Feature icon={<KeyRound className="size-4" />} title="Hashed keys, audited actions">
            API secrets are shown once and stored as hashes. Approvals, overrides, price changes and key rotations create immutable audit records.
          </Feature>
          <Feature icon={<Lock className="size-4" />} title="Tenant isolation">
            Identity is resolved server-side on every request; cache scopes are explicit so one consumer's results are never served to another.
          </Feature>
        </div>
      </Section>

      {/* AI Data Architect */}
      <Section eyebrow="AI Data Architect" title="See exactly what will leave your environment." lede="The architect inspects schema metadata and masked samples — not your dataset — and proposes a sanitised serving model. Nothing activates without explicit approval.">
        <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr]">
          <div className="overflow-hidden rounded-lg border border-border bg-panel">
            <div className="grid grid-cols-[1fr_auto_1fr] border-b border-border px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-fg-3">
              <span>Source</span>
              <span />
              <span>Serving</span>
            </div>
            <ul className="font-mono text-[13px]">
              {MAPPING.map(([s, t, kind]) => (
                <li key={s} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-border/60 px-4 py-2 last:border-0">
                  <span className="text-fg">{s}</span>
                  <ArrowRight className="size-3.5 text-fg-3" aria-hidden />
                  <span className={kind === "shared" ? "text-[#4cd14c]" : kind === "transformed" ? "text-[#7fb2f0]" : kind === "prohibited" ? "text-[#ff8080]" : "text-serious"}>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <ul className="space-y-4 text-sm text-fg-2">
            {[
              ["Classifies every field", "PUBLIC, BUSINESS_DATA, PII, FINANCIAL, HEALTH, AUTHENTICATION_SECRET, PROHIBITED and more — including risky combinations of innocent fields."],
              ["Proposes a serving model", "Entities, relationships, capabilities, indexes, freshness and cache policy optimised for the questions AI will ask."],
              ["Transforms, not just filters", "Include, rename, aggregate, bucket, round, hash, tokenise, redact or generalise any field."],
              ["Simulates hostile questions", "Type “Tell me John Smith's home address” and see why the query is impossible."],
            ].map(([h, t]) => (
              <li key={h} className="flex gap-3">
                <Brain className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
                <span>
                  <span className="font-medium text-fg">{h}.</span> {t}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Global Data Index + sync */}
      <Section eyebrow="Global Data Index" title="DNS for machine-readable data." lede="AI shouldn't need to know a provider before asking a question. Every data product publishes a signed manifest; discovery searches the compact catalogue — never every database.">
        <div className="grid gap-4 md:grid-cols-4">
          {[
            [Compass, "Understand", "Intent, entities, geography, required freshness."],
            [Network, "Discover", "Catalogue search over manifests and capabilities."],
            [Route, "Rank", "Quality, freshness, latency, price, exclusivity."],
            [Zap, "Route", "Nearest serving shard or edge cache."],
          ].map(([I, h, t]) => {
            const Icon = I as typeof Compass;
            return (
              <div key={h as string} className="rounded-lg border border-border bg-panel p-5">
                <Icon className="size-5 text-accent" aria-hidden />
                <h3 className="mt-3 text-sm font-semibold">{h as string}</h3>
                <p className="mt-1 text-sm text-fg-2">{t as string}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section eyebrow="Real-time sync" title="Freshness you can measure, not just claim." lede="Initial snapshot, then incremental change capture. Every hop is timestamped, so actual lag is published in each manifest and on every response.">
        <div className="rounded-lg border border-border bg-panel p-6">
          <div className="grid gap-4 sm:grid-cols-4">
            {[
              ["Source → Connector", "800ms"],
              ["Connector → DBIndex", "150ms"],
              ["Indexing", "90ms"],
              ["Total lag", "1.04s"],
            ].map(([k, v], i) => (
              <div key={k} className={i === 3 ? "rounded-md border border-accent/40 bg-accent-soft p-4" : "rounded-md border border-border bg-bg-elev p-4"}>
                <div className="text-xs text-fg-3">{k}</div>
                <div className="tabular mt-1 text-xl font-semibold">{v}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 flex items-center gap-2 text-xs text-fg-3">
            <Clock className="size-3.5" aria-hidden /> Illustrative breakdown. If a connector falls behind, DBIndex reports the real lag and freshness-constrained queries route elsewhere.
          </p>
        </div>
      </Section>

      {/* Marketplace + LLM providers */}
      <Section eyebrow="Data marketplace" title="Data products, priced per query.">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-border bg-panel p-6">
            <Store className="size-5 text-accent" aria-hidden />
            <h3 className="mt-3 text-base font-semibold">For data buyers</h3>
            <p className="mt-2 text-sm text-fg-2">Search by category, geography, freshness, quality and price. Every dataset shows its schema, coverage, licence terms, live freshness and competition.</p>
            <ButtonLink to="/marketplace" className="mt-5" variant="outline" size="sm">
              Browse the marketplace <ArrowRight className="size-3.5" />
            </ButtonLink>
          </div>
          <div className="rounded-lg border border-border bg-panel p-6">
            <Sparkles className="size-5 text-accent" aria-hidden />
            <h3 className="mt-3 text-base font-semibold">For LLM providers and agent builders</h3>
            <p className="mt-2 text-sm text-fg-2">
              One API and one MCP server for every provider on the network. Budgets, scoped keys, per-query price caps and provenance on every answer — with usage rights (live query, RAG, training) returned explicitly.
            </p>
            <ButtonLink to="/docs#integrations" className="mt-5" variant="outline" size="sm">
              Integration guides <ArrowRight className="size-3.5" />
            </ButtonLink>
          </div>
        </div>
      </Section>

      {/* Revenue calculator */}
      <Section id="calculator" eyebrow="Revenue calculator" title="Model your data product's economics." lede="Inputs and assumptions are all visible. Estimates use the same versioned infrastructure price table as the provider dashboard.">
        <RevenueCalculator />
      </Section>

      {/* Pricing / infra economics */}
      <Section eyebrow="Pricing" title="Usage-based, with near-zero idle cost.">
        <div className="grid gap-4 md:grid-cols-3">
          <PriceCol title="Data providers" items={["No platform subscription to publish", "Marketplace fee on query revenue", "Hosting cost shown before activation", "Costs and earnings on one ledger"]} />
          <PriceCol title="AI consumers" items={["Pay per successful query", "Prepaid credits or billing", "Monthly budgets and per-query caps", "Cached answers at the same price, faster"]} />
          <PriceCol title="Enterprise" items={["Live origin queries for extreme freshness", "Data residency (roadmap)", "Two-person approvals, SSO/SCIM (roadmap)", "Reserved capacity"]} />
        </div>
        <ButtonLink to="/pricing" className="mt-6" variant="ghost" size="sm">
          Full pricing <ArrowRight className="size-3.5" />
        </ButtonLink>
      </Section>

      {/* Supported databases */}
      <Section eyebrow="Supported databases" title="Adapters, not special cases.">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {[
            ["PostgreSQL", "Available"],
            ["MySQL", "Available"],
            ["MariaDB", "Coming soon"],
            ["SQL Server", "Coming soon"],
            ["Oracle", "Coming soon"],
            ["SQLite", "Coming soon"],
            ["SAP / Sybase", "Planned"],
            ["Ingres", "Planned"],
          ].map(([n, s]) => (
            <div key={n} className="rounded-lg border border-border bg-panel p-4">
              <Database className="size-4 text-fg-3" aria-hidden />
              <div className="mt-2 text-sm font-medium">{n}</div>
              <div className={s === "Available" ? "mt-0.5 text-xs text-[#4cd14c]" : "mt-0.5 text-xs text-fg-3"}>{s}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-fg-3">Future adapters: MongoDB, DynamoDB, Cassandra, CockroachDB, ClickHouse, Snowflake, BigQuery, Redshift, Databricks, Elasticsearch/OpenSearch and more.</p>
      </Section>

      {/* Developer API + MCP */}
      <Section eyebrow="Developer API" title="One request. Provenance included.">
        <div className="grid gap-4 lg:grid-cols-2">
          <CodeBlock code={CURL} title="POST /v1/query" />
          <CodeBlock code={RESPONSE} title="200 OK" />
        </div>
      </Section>
      <Section id="mcp" eyebrow="MCP" title="Native to agents.">
        <div className="grid gap-8 lg:grid-cols-2">
          <ul className="space-y-3 text-sm text-fg-2">
            {[
              ["dbindex.discover", "What live databases could answer this?"],
              ["dbindex.describe", "Schema, capabilities, freshness, rights."],
              ["dbindex.query", "Use the best source under $0.002."],
              ["dbindex.compare_sources", "Rank candidates side by side."],
              ["dbindex.estimate_cost", "Price before you pay."],
            ].map(([t, d]) => (
              <li key={t} className="flex gap-3">
                <code className="shrink-0 rounded bg-panel-2 px-1.5 py-0.5 font-mono text-xs text-accent">{t}</code>
                <span>{d}</span>
              </li>
            ))}
            <li className="pt-2 text-xs text-fg-3">MCP honours the same API key scopes, budgets and price caps as REST.</li>
          </ul>
          <CodeBlock code={MCP} title="mcp.json" />
        </div>
      </Section>

      {/* CTA */}
      <section className="border-t border-border bg-bg-elev py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">The data network for AI agents.</h2>
          <p className="mt-3 text-fg-2">Query live proprietary data through one API — or publish yours and get paid when AI uses it.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <ButtonLink to="/developers" variant="primary" size="lg">
              Get API key
            </ButtonLink>
            <ButtonLink to="/partners/founding-data" variant="outline" size="lg">
              Become a Founding Data Partner
            </ButtonLink>
          </div>
        </div>
      </section>
    </>
  );
}

function SideCard({ eyebrow, lines, body, cta, icon }: { eyebrow: string; lines: string[]; body: string; cta: [string, string]; icon: ReactNode }) {
  return (
    <div className="flex flex-col rounded-xl border border-border bg-panel p-6">
      <div className="flex items-center gap-3">
        <span className="flex size-9 items-center justify-center rounded-md bg-accent-soft text-accent" aria-hidden>{icon}</span>
        <h2 className="font-mono text-xs uppercase tracking-[0.16em] text-fg-3">{eyebrow}</h2>
      </div>
      <p className="mt-4 text-2xl font-semibold leading-snug tracking-tight">{lines.map((l) => <span key={l} className="block">{l}</span>)}</p>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-fg-2">{body}</p>
      <ButtonLink to={cta[1]} variant="primary" size="md" className="mt-5 self-start">{cta[0]} <ArrowRight className="size-4" aria-hidden /></ButtonLink>
    </div>
  );
}

function Flow({ id, title, steps }: { id: string; title: string; steps: [string, string][] }) {
  return (
    <div id={id} className="rounded-lg border border-border bg-panel p-6">
      <h3 className="font-mono text-xs uppercase tracking-[0.16em] text-fg-3">{title}</h3>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-lg font-semibold">
        {steps.map(([s], i) => (
          <span key={s} className="flex items-center gap-2">
            {s}
            {i < steps.length - 1 && <ArrowRight className="size-4 text-accent" aria-hidden />}
          </span>
        ))}
      </div>
      <ol className="mt-5 space-y-3">
        {steps.map(([s, d], i) => (
          <li key={s} className="flex gap-3 text-sm">
            <span className="tabular flex size-5 shrink-0 items-center justify-center rounded-full border border-border-strong text-[10px] text-fg-2">{i + 1}</span>
            <span className="text-fg-2">
              <span className="font-medium text-fg">{s}.</span> {d}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function PriceCol({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-lg border border-border bg-panel p-6">
      <h3 className="text-sm font-semibold">{title}</h3>
      <ul className="mt-4 space-y-2 text-sm text-fg-2">
        {items.map((i) => (
          <li key={i} className="flex gap-2">
            <Check className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
            {i}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function NotFound() {
  return (
    <div className="mx-auto max-w-lg px-4 py-24 text-center">
      <p className="font-mono text-sm text-accent">404</p>
      <h1 className="mt-2 text-2xl font-semibold">Page not found</h1>
      <p className="mt-2 text-fg-2">The route does not exist or you do not have access to it.</p>
      <Link to="/" className="mt-6 inline-block text-sm text-accent hover:underline">
        Back to home
      </Link>
    </div>
  );
}
